/**
 * Customer motor private — wraps existing quotation / payment APIs (prod 3437).
 */

import axios from 'axios';
import { MOTOR_PROD_ID } from '@/constants/motorPrivate';
import buildMotorQuotationPolicyDto from '@/utils/buildMotorQuotationPolicyDto';

function unwrap(response) {
  const body = response?.data;
  if (body && typeof body.success === 'boolean') {
    return body;
  }
  return { success: true, data: body?.data ?? body };
}

/** PUT /update often returns 200 with quote payload and no success flag (legacy portal). */
function unwrapUpdate(response) {
  const body = response?.data;
  if (body?.success === false) {
    const msg = body.message || body.msg
      || (Array.isArray(body.messages) && body.messages[0])
      || 'Update failed';
    return { success: false, message: msg };
  }
  if (body?.success === true) {
    return { success: true, data: body.data ?? body };
  }
  if (response?.status >= 200 && response?.status < 300) {
    const payload = body?.data ?? body;
    if (payload?.covertypes || payload?.quoteId) {
      return { success: true, data: payload };
    }
    if (body?.covertypes || body?.quoteId) {
      return { success: true, data: body };
    }
    return { success: true, data: payload };
  }
  return { success: false, message: 'Update failed' };
}

function fail(err, fallback = 'Request failed') {
  const msg = err?.response?.data?.msg
    || err?.response?.data?.message
    || err?.message
    || fallback;
  return { success: false, message: msg };
}

export async function createMotorQuote({
  make,
  model,
  yearOfManufacture,
  valueOfCar,
  email,
  fullName,
  kraPinNumber,
  agentId = 0,
}) {
  try {
    const res = await axios.post(
      '/customer/quotation/create',
      {
        origin: 'P',
        agentId: Number(agentId) || 0,
        prodId: MOTOR_PROD_ID,
        email: email || '',
        fullName: fullName || '',
        kraPinNumber: kraPinNumber || '',
        carDTOS: [{
          carId: 0,
          coverTypeId: 0,
          evaluationSelected: -1,
          hasInsurance: false,
          make,
          model,
          riskId: 0,
          seatingCapacity: 0,
          valueOfCar: parseInt(valueOfCar, 10) || 0,
          valueOfEntertainmentScreen: 0,
          valueOfWindScreen: 0,
          vehicleImages: [],
          yearOfManufacture,
        }],
      },
      { _skipErrorRedirect: true },
    );
    return unwrap(res);
  } catch (e) {
    return fail(e, 'Failed to create quote');
  }
}

export async function getMotorQuote(quoteId) {
  try {
    const res = await axios.get('/customer/quotation', {
      params: { quoteId },
      _skipErrorRedirect: true,
    });
    return unwrap(res);
  } catch (e) {
    return fail(e, 'Failed to load quote');
  }
}

export async function updateMotorQuote(payload) {
  try {
    const agentId = payload?.agentId != null ? Number(payload.agentId) || 0 : 0;
    const res = await axios.put(
      '/customer/quotation/update',
      { ...payload, agentId, prodId: payload.prodId ?? MOTOR_PROD_ID },
      { _skipErrorRedirect: true },
    );
    return unwrapUpdate(res);
  } catch (e) {
    return fail(e, 'Failed to update quote');
  }
}

/** Agent-aligned quotations policy init — GIS runs after payment, not here. */
export const MOTOR_POLICY_INIT_PATH = '/customer/quotation/policy/create';

const POLICY_INIT_FAIL_MSG = 'Failed to initialize policy';

function unwrapPolicyInit(response) {
  const body = response?.data;
  if (body?.success === false) {
    return {
      success: false,
      message: body.message || body.msg || POLICY_INIT_FAIL_MSG,
    };
  }
  if (body?.success === true) {
    return { success: true, data: body.data ?? body };
  }
  if (body?.data?.refno) {
    return { success: true, data: body.data };
  }
  return { success: false, message: POLICY_INIT_FAIL_MSG };
}

/**
 * Register quotations-api policy before payment (same flow as agent motor — GIS after payment).
 * @param {object} params — passed to buildMotorQuotationPolicyDto when payload omitted
 */
export async function initMotorQuotationPolicy(paramsOrPayload) {
  const payload = paramsOrPayload?.carDTOS
    ? paramsOrPayload
    : buildMotorQuotationPolicyDto(paramsOrPayload);
  try {
    const res = await axios.post(
      MOTOR_POLICY_INIT_PATH,
      payload,
      { _skipErrorRedirect: true },
    );
    return unwrapPolicyInit(res);
  } catch (e) {
    return fail(e, POLICY_INIT_FAIL_MSG);
  }
}

export async function listMotorBranches() {
  try {
    const res = await axios.get('/customer/gis/branches/retrieveAll', { _skipErrorRedirect: true });
    const list = res?.data?.object || res?.data?.data || [];
    return { success: true, data: list };
  } catch (e) {
    return fail(e, 'Failed to load branches');
  }
}

export async function getVehicleMakes() {
  try {
    const res = await axios.get('/customer/gis/vehicles/make/retrieveAll', { _skipErrorRedirect: true });
    return { success: true, data: res?.data?.object || [] };
  } catch (e) {
    return fail(e, 'Failed to load vehicle makes');
  }
}

export async function getVehicleModels(vmkCode) {
  try {
    const res = await axios.get(`/customer/gis/vehicles/${vmkCode}/models`, { _skipErrorRedirect: true });
    return { success: true, data: res?.data?.object || [] };
  } catch (e) {
    return fail(e, 'Failed to load models');
  }
}

function normalizeMpesaPhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.startsWith('0') && digits.length === 10) return `254${digits.slice(1)}`;
  if (digits.startsWith('254')) return digits;
  if (digits.startsWith('7') && digits.length === 9) return `254${digits}`;
  return digits;
}

export async function requestMotorStkPush({
  quoteId,
  payableAmount,
  customerPhoneNumber,
  transactionReference,
  transactionDescription,
}) {
  const phone = normalizeMpesaPhone(customerPhoneNumber);
  if (phone.length < 12) {
    return { success: false, message: 'Enter a valid M-Pesa phone number' };
  }
  try {
    const res = await axios.post(
      '/customer/payment/client-api/v1/payments/mpesa/stk/request',
      {
        serviceType: 'PORTAL',
        settlementProduct: 'MOTOR_RFQ',
        quoteId: Number(quoteId),
        payableAmount: Math.max(1, Math.round(Number(payableAmount) || 0)),
        customerPhoneNumber: phone,
        transactionReference: String(transactionReference || '').replace(/\s+/g, '').slice(0, 12),
        transactionDescription: String(transactionDescription || 'motor').slice(0, 13),
      },
      { _skipErrorRedirect: true },
    );
    const body = res?.data;
    if (body?.success && body?.data?.paymentId) {
      return { success: true, data: { paymentId: body.data.paymentId } };
    }
    return { success: false, message: body?.message || 'Failed to trigger M-Pesa' };
  } catch (e) {
    return fail(e, 'M-Pesa STK failed');
  }
}

export async function pollMotorPaymentStatus(paymentId) {
  try {
    const res = await axios.get(
      '/customer/payment/client-api/v1/payments/mpesa/payment/query',
      { params: { paymentId }, _skipErrorRedirect: true },
    );
    const body = res?.data;
    if (body?.success) {
      return { success: true, data: { paymentStatus: body.data?.paymentStatus } };
    }
    return { success: false, message: body?.message || 'Could not check payment' };
  } catch (e) {
    return fail(e, 'Payment status check failed');
  }
}

export async function validateMotorPaybillPayment(accountNumber) {
  try {
    const res = await axios.get('/customer/gis/payments/validatePayment', {
      params: { accountNumber },
      _skipErrorRedirect: true,
      validateStatus: () => true,
    });
    const body = res?.data;
    if (body?.success) {
      return { success: true, message: body.message, data: body };
    }
    return {
      success: false,
      message: body?.message || 'Payment not yet received. Complete Paybill payment and try again.',
    };
  } catch (e) {
    return fail(e, 'Payment validation failed');
  }
}

export function findRiskByCovertypeId(quote, covertypeId) {
  const types = quote?.covertypes || [];
  return types.find((c) => Number(c.covertypeId) === Number(covertypeId)) || null;
}

export function getRiskPremium(risk) {
  return risk?.riskPremDto?.totalPremium
    ?? risk?.riskPremDto?.total
    ?? 0;
}
